import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";

const MIN_SCALE = 1;
const MAX_SCALE = 5;

export default function ImageZoomModal({
  src,
  thumbnailSrc,
  alt = "Profile picture",
  size = 64,
  borderRadius = 50,
  allowDownload = true,
  fileName = "profile-image.jpg",
}) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const [portalEl, setPortalEl] = useState(null);

  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    setPortalEl(el);

    return () => {
      document.body.removeChild(el);
    };
  }, []);

  const dragState = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
  });

  const closeModal = useCallback(() => {
    setOpen(false);
    setScale(1);
    setPos({ x: 0, y: 0 });
    setDownloadError(null);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") closeModal();
    },
    [closeModal],
  );

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    setScale((s) => clamp(s + delta * s, MIN_SCALE, MAX_SCALE));
  };

  const onDoubleClick = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragState.current.dragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPos({
      x: dragState.current.origX + dx,
      y: dragState.current.origY + dy,
    });
  };

  const onPointerUp = () => {
    dragState.current.dragging = false;
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(src, { mode: "cors" });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Common cause: the image host doesn't send permissive CORS headers.
      setDownloadError(
        "Couldn't download the image directly. Opening it in a new tab instead.",
      );
      window.open(src, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <img
        src={thumbnailSrc || src}
        alt={alt}
        onClick={() => setOpen(true)}
        style={{
          width: size,
          height: size,
          borderRadius: `${borderRadius}%`,
          objectFit: "cover",
          cursor: "zoom-in",
        }}
      />

      {open &&
        portalEl &&
        ReactDOM.createPortal(
          <div
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            style={styles.overlay}
          >
            <button
              onClick={closeModal}
              aria-label="Close"
              style={styles.closeBtn}
            >
              ✕
            </button>

            <div
              style={styles.imageWrap}
              onWheel={onWheel}
              onDoubleClick={onDoubleClick}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              <img
                src={src}
                alt={alt}
                draggable={false}
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                  transition: dragState.current.dragging
                    ? "none"
                    : "transform 0.05s linear",
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  cursor: scale > 1 ? "grab" : "zoom-in",
                  userSelect: "none",
                }}
              />
            </div>

            <div style={styles.controls}>
              <button
                style={styles.controlBtn}
                onClick={() =>
                  setScale((s) => clamp(s - 0.5, MIN_SCALE, MAX_SCALE))
                }
              >
                −
              </button>
              <span
                style={{ color: "#fff", minWidth: 48, textAlign: "center" }}
              >
                {Math.round(scale * 100)}%
              </span>
              <button
                style={styles.controlBtn}
                onClick={() =>
                  setScale((s) => clamp(s + 0.5, MIN_SCALE, MAX_SCALE))
                }
              >
                +
              </button>

              {allowDownload && (
                <button
                  style={{
                    ...styles.controlBtn,
                    width: "auto",
                    padding: "0 16px",
                  }}
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? "Downloading…" : "Download"}
                </button>
              )}
            </div>

            {downloadError && (
              <div style={styles.errorText}>{downloadError}</div>
            )}
          </div>,
          portalEl,
        )}
    </>
  );
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.9)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "#fff",
    fontSize: 20,
    width: 40,
    height: 40,
    borderRadius: "50%",
    cursor: "pointer",
  },
  imageWrap: {
    flex: 1,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    touchAction: "none",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 0 24px",
  },
  controlBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    border: "none",
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    fontSize: 18,
    cursor: "pointer",
  },
  errorText: {
    color: "#f88",
    fontSize: 13,
    paddingBottom: 12,
  },
};
